import { Button, Form, Table } from 'antd';
import { useEffect, useState } from 'react';
import { useUserPages } from '../../../hooks/usePages';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAuthRequest from '../../../services/requests.service';
import { Bounce, toast } from 'react-toastify';
import { useOfferReducer } from '../../../hooks/useOfferReducer';

const columns = [
    {
        title: 'Page',
        dataIndex: 'name',
        render: (text) => <a>{text}</a>,
    },
];

const CreateOffer3 = ({setDeals, setIsLoading, setIsModalOpen, page, currentDeal }) => {
    const { data: pagesData, isLoading: pagesLoading, error: pagesError } = useUserPages();
    const pages = pagesData?.pages || [];
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const request = useAuthRequest();
    const { formData, updateMultipleFields } = useOfferReducer();
    const queryClient = useQueryClient();

    const [form] = Form.useForm();
    const { postDataAPIWithoutToken } = useAuthRequest();
    form.setFieldsValue(formData);

    // Mutation pour créer/modifier une offre
    const createOfferMutation = useMutation({
        mutationFn: async (offerData) => {
            // Si on a un currentDeal, c'est une modification, sinon une création
            if (offerData._id) {
                const res = await request.putDataAPI(`/deal/${offerData._id}`, offerData);
                return res;
            } else {
                const res = await request.postDataAPI('/deal', offerData);
                return res;
            }
        },
        onSuccess: (data, offerData) => {
            setIsModalOpen(false);
            // Invalider le cache des deals pour forcer un refetch
            queryClient.invalidateQueries(['dealsAll']);
            if (setDeals) {
                getDeals();
            }
            
            const message = offerData._id ? "Offre modifiée!" : "Offre postée!";
            toast.success(message, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        },
        onError: (error) => {
            const message = error.msg || "Erreur lors de l'opération sur l'offre";
            toast.error(message, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        }
    });

    useEffect(() => {
        // Preselect the row where the page._id matches
        if (pages.length > 0) {
            const preselectedKeys = pages
                .filter(p => p._id === page._id)
                .map(p => p._id);

            setSelectedRowKeys(preselectedKeys);
        }
    }, [pages, page]);

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
        const selectedPages = pages.filter(p => newSelectedRowKeys.includes(p._id));
        updateMultipleFields({ pages: selectedPages });
    };

    const getDeals = async () => {
        let res = await request.getDataAPI('/dealsByCompany/'+page._id)
        setDeals(res)
        console.log(res)
    }

    const submit = async () => {
        console.log(formData);
        setIsLoading(true);

        try {
            let finalFormData = { ...formData };

            // Upload images if any
            if (formData.images && formData.images.length > 0) {
                let media = await imageUpload(formData.images);
                if (Array.isArray(media) && media.length > 0 && media[0].url) {
                    finalFormData.images = media;
                }
            }

            // Upload PDF if any
            if (formData.filePdf && formData.filePdf.length > 0) {
                let media = await pdfUpload(formData.filePdf);
                if (Array.isArray(media) && media.length > 0 && media[0].url) {
                    finalFormData.filePdf = media;
                }
            }

            finalFormData.pages = selectedRowKeys;
            
            // Si on modifie une offre existante, inclure l'ID
            if (currentDeal && currentDeal._id) {
                finalFormData._id = currentDeal._id;
            }
            
            // Use mutation to submit the offer
            createOfferMutation.mutate(finalFormData);
        } catch (error) {
            console.error('Error preparing offer data:', error);
            toast.error("Erreur lors de la préparation des données", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const imageUpload = async (images) => {
        console.log("image upload");
        let imgArr = [];

        for (const item of images) {
            const formDataS3 = new FormData();
            formDataS3.append("image", item.originFileObj);

            console.log(JSON.stringify(formDataS3))
            try {
                const resS3 = await postDataAPIWithoutToken('uploadImage', formDataS3);
                console.log(resS3)
                imgArr.push({ url: resS3.uploadLocation });
            } catch (error) {
                console.error("Image upload failed:", error);
            }
        }

        return imgArr;
    };

    const pdfUpload = async (pdfFiles) => {
        console.log("PDF upload");
        console.log(pdfFiles)
        let pdfArr = [];

        for(const item of pdfFiles){
            const formDataS3 = new FormData();    
            // Directly append the PDF file to formData
            formDataS3.append("file", item, item.name);
            
            // Here, adjust the parameters as necessary for your backend requirements
            formDataS3.append("upload_preset", "bb5qshs3"); // Adjust or remove if not needed
            formDataS3.append("cloud_name", "behandy"); // Adjust or remove if not needed
    
            console.log(formDataS3)
            // Replace 'uploadFile' with your actual backend endpoint for file uploads
            const resS3 = await postDataAPIWithoutToken('uploadFile', formDataS3);
            console.log(resS3)
            // Ensure the response handling matches your backend's response structure
            pdfArr.push({url: resS3.uploadLocation});
        }

        return pdfArr;
    }
    
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record) => ({
            name: record.name,
        }),
    };

    // Loading state pour les pages
    if (pagesLoading) {
        return (
            <div className='flex flex-col w-full h-full justify-center items-center'>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement des pages...</p>
                </div>
            </div>
        );
    }

    // Error state pour les pages
    if (pagesError) {
        return (
            <div className='flex flex-col w-full h-full justify-center items-center'>
                <div className="text-center">
                    <p className="text-red-600 mb-4">Erreur lors du chargement des pages</p>
                    <Button onClick={() => window.location.reload()}>
                        Réessayer
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className='flex flex-col w-full h-full'>

            <Table
                rowSelection={{
                    type: 'checkbox',
                    ...rowSelection,
                }}
                columns={columns}
                dataSource={pages}
                rowKey="_id"
                pagination={false}
                className="mb-6"
            />

            <div className="flex justify-end space-x-4 mt-6">
                <Button 
                    onClick={() => setIsModalOpen(false)}
                    className="px-6"
                >
                    Annuler
                </Button>
                <Button 
                    type="primary" 
                    onClick={submit}
                    loading={createOfferMutation.isPending}
                    className="px-6 bg-royalblue-500 hover:bg-royalblue-600"
                >
                    {createOfferMutation.isPending ? 'Création...' : 'Créer l\'offre'}
                </Button>
            </div>
        </div>
    );
};

export default CreateOffer3;
